import { OpenLocationCode } from 'open-location-code'

import { loadGoogleMaps } from '../google-maps/loader'

const PLUS_CODE_PATTERN = /[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,7}/i

export const inputLooksLikePlusCode = (input: string): boolean => PLUS_CODE_PATTERN.test(input.trim())

export const extractPlusCode = (input: string): string => {
  const match = input.trim().match(PLUS_CODE_PATTERN)
  return match ? match[0].toUpperCase() : input.trim().toUpperCase()
}

export const extractPlusCodeLocality = (input: string): string | null => {
  const trimmed = input.trim()
  const match = trimmed.match(PLUS_CODE_PATTERN)
  if (!match || match.index === undefined) {
    return null
  }

  const locality = trimmed.slice(match.index + match[0].length).trim()
  return locality || null
}

export interface PlusCodeAddressParts {
  city: string
  state: string
  zip: string
  county: string
  country: string
  countryCode: string
}

export const EMPTY_PLUS_CODE_ADDRESS_PARTS: PlusCodeAddressParts = {
  city: '',
  state: '',
  zip: '',
  county: '',
  country: '',
  countryCode: '',
}

const toAddressParts = (result?: google.maps.GeocoderResult | null): PlusCodeAddressParts => {
  if (!result?.address_components) {
    return EMPTY_PLUS_CODE_ADDRESS_PARTS
  }

  const longNames = new Map<string, string>()
  const shortNames = new Map<string, string>()
  result.address_components.forEach(component => {
    component.types.forEach(type => {
      longNames.set(type, component.long_name)
      shortNames.set(type, component.short_name)
    })
  })

  return {
    city:
      longNames.get('locality') ||
      longNames.get('postal_town') ||
      longNames.get('sublocality') ||
      longNames.get('sublocality_level_1') ||
      '',
    state: longNames.get('administrative_area_level_1') || '',
    zip: longNames.get('postal_code') || '',
    county: longNames.get('administrative_area_level_2') || '',
    country: longNames.get('country') || '',
    countryCode: shortNames.get('country') || '',
  }
}

const geocode = async (request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult | null> => {
  await loadGoogleMaps()
  const geocoder = new google.maps.Geocoder()

  return new Promise(resolve => {
    geocoder.geocode(request, (results, status) => {
      resolve(status === 'OK' && results?.length ? results[0] : null)
    })
  })
}

export const geocodePlusCodeLocality = async (locality: string): Promise<{ lat: number; lng: number } | null> => {
  const result = await geocode({ address: locality })
  if (!result?.geometry?.location) {
    return null
  }

  const location = result.geometry.location
  return { lat: location.lat(), lng: location.lng() }
}

// A Plus Code carries no address components of its own, so the surrounding
// locality has to be recovered from the point it decodes to.
const resolveAddressParts = async (lat: number, lng: number): Promise<PlusCodeAddressParts> => {
  try {
    return toAddressParts(await geocode({ location: { lat, lng } }))
  } catch {
    return EMPTY_PLUS_CODE_ADDRESS_PARTS
  }
}

export const resolvePlusCodeToCoordinates = async ({
  input,
  referenceCoordinates,
}: {
  input: string
  referenceCoordinates?: { lat: number; lng: number }
}): Promise<
  { plusCode: string; lat: number; lng: number; address: PlusCodeAddressParts } | { plusCode: string; error: string }
> => {
  const plusCode = extractPlusCode(input)

  if (!plusCode) {
    return { plusCode: '', error: 'Enter a valid Plus Code.' }
  }

  const openLocationCode = new OpenLocationCode()
  if (!openLocationCode.isValid(plusCode)) {
    return { plusCode, error: 'Enter a valid Plus Code.' }
  }

  try {
    if (!openLocationCode.isShort(plusCode)) {
      const area = openLocationCode.decode(plusCode)
      return {
        plusCode,
        lat: area.latitudeCenter,
        lng: area.longitudeCenter,
        address: await resolveAddressParts(area.latitudeCenter, area.longitudeCenter),
      }
    }

    const locality = extractPlusCodeLocality(input)
    let reference = referenceCoordinates

    if (locality) {
      const geocodedLocality = await geocodePlusCodeLocality(locality)
      if (geocodedLocality) {
        reference = geocodedLocality
      }
    }

    if (!reference) {
      return {
        plusCode,
        error:
          'Could not resolve this Plus Code. Paste it exactly as copied from Google Maps (including the city), or set your organization address in Settings.',
      }
    }

    const fullCode = openLocationCode.recoverNearest(plusCode, reference.lat, reference.lng)
    const area = openLocationCode.decode(fullCode)
    return {
      plusCode,
      lat: area.latitudeCenter,
      lng: area.longitudeCenter,
      address: await resolveAddressParts(area.latitudeCenter, area.longitudeCenter),
    }
  } catch {
    return { plusCode, error: 'Enter a valid Plus Code.' }
  }
}
